/**
 * Five-star quality rating: rates the exchange, never the person
 * (`docs/CONVENTIONS.md` §8). The colour never carries the meaning alone —
 * a written label always accompanies the filled stars.
 */
import { t } from '../../../i18n/fr';
import { STAR_LABEL_KEYS } from '../labels';

export interface StarRatingProps {
  /** Current rating, 1-5, or `null` when none was given yet. */
  value: number | null;
  /** Called with the star index (1-5) the caller picked. */
  onChange: (value: number) => void;
  disabled?: boolean;
  /** Passed through to the group's `id`, for a field this rating belongs to. */
  id?: string;
}

const STARS = [1, 2, 3, 4, 5];

/**
 * Render the five-star quality rating and its written readout.
 *
 * @param props - Current value, change handler and optional disabled state.
 * @returns The rating element.
 */
export function StarRating({ value, onChange, disabled = false, id }: StarRatingProps) {
  return (
    <div id={id}>
      <div className="etoiles" role="group" aria-label={t('household.stars.groupLabel')}>
        {STARS.map((n) => {
          const on = value !== null && n <= value;
          return (
            <button
              key={n}
              type="button"
              className={on ? 'on' : ''}
              aria-pressed={on}
              aria-label={t('household.stars.optionLabel', { n, plural: n === 1 ? '' : 's' })}
              onClick={() => onChange(n)}
              disabled={disabled}
            >
              ★
            </button>
          );
        })}
      </div>
      {value !== null ? (
        <p className="etoiles-lu">
          {t('household.stars.readout', { n: value, label: t(STAR_LABEL_KEYS[value]) })}
        </p>
      ) : null}
    </div>
  );
}
